import { CSSProperties, Fragment, useContext } from 'react';
import { clsx, makeKey, styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import useScreen from 'Hooks/useScreen';
import Sty from 'modules/Sty';
import Icon from './Icon';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import Loader from './Loader';

/** Props for rendering each tab item */
export interface TabItem {
    /** Unique identifier for the tab */
    id: string;
    /** Display title for the tab */
    title: string;
    /** Optional custom render function for the tab number/icon */
    renderNumber?: (index: number) => React.ReactNode;
    /** Whether the tab is pro only */
    isProOnly?: boolean;
    /** Color of the tab */
    color?: string;
    /** Whether the tab is active */
    isColorEnabled?: boolean;
}

interface TabsSymphonyProps {
    className: string;
    style?: CSSProperties;
    /** Array of tab items to display */
    styleHeader?: CSSProperties;
    tabs: TabItem[];
    /** Currently selected tab id */
    selectedTab: string | TabItem;
    /** Callback when tab selection changes */
    onChange?: (tab: string | TabItem) => void;
    /** Type of tabs to display */
    type?: 'tabs' | 'dots' | 'numbers';
    /** Whether the tabs are in the header */
    isHeader?: boolean;
    /** Whether to hide the bottom border of the tabs component */
    isBorderBottom?: boolean;
    /** Whether to add a margin bottom to the tabs component */
    isMarginBottom?: boolean;
    /** Whether to show a right side content */
    rightSideContent?: React.ReactNode;
    /** Whether to show a loading state */
    isLoading?: boolean;
}

const TabsSymphony = ({
    className,
    style,
    styleHeader,
    tabs,
    selectedTab,
    onChange,
    type = 'tabs',
    isHeader = false,
    isBorderBottom = false,
    isMarginBottom = false,
    rightSideContent,
    isLoading = false,
}: TabsSymphonyProps) => {
    const { mStyle } = useScreen();
    const { isProBrand } = useCurrentBrand();
    const { handleOpenModal } = useContext(UpgradeModalContext)

    const handleOnChange = (tab: TabItem) => {
        onChange && onChange(tab);
    }

    const renderLoader = () => {
        if (isLoading) {
            return <Loader className="tabsSymphonyLoader" />;
        }
    }

    const renderRightSideContent = () => {
        if (rightSideContent) {
            return rightSideContent;
        }
    }

    /** Renders the number or checkmark for each tab based on its state */
    const renderTabNumber = (index: number, isActive: boolean) => {
        const isPastStep = tabs.findIndex(tab =>
            tab.id === (typeof selectedTab === 'string' ? selectedTab : selectedTab?.id)
        ) > index;

        let backgroundColor = Colors.grey;
        if (isActive) {
            backgroundColor = Colors.purple;
        } else if (isPastStep) {
            backgroundColor = Colors.green;
        }

        const number = index + 1;

        return (
            <div
                className="campaignNumber"
                style={{
                    ...sty.campaignNumber,
                    backgroundColor
                }}
            >
                {isPastStep ? '✓' : number}
            </div>
        );
    };

    const tabsComponent = (
        <div
            className={clsx('tabsSymphonyElement', className)}
            style={styx(
                mStyle(sty, 'tabsSymphonyElement'),
                isBorderBottom && { borderBottom: Sty.border },
                isHeader && { marginLeft: 'auto', marginRight: 'auto' },
                isMarginBottom && { marginBottom: 16 },
                style
            )}
        >
            {tabs.map((tab, index) => {
                const isActive = typeof selectedTab === 'string' ? selectedTab === tab.id : selectedTab?.id === tab.id;

                return (
                    <Fragment key={makeKey(index, tab.id)}>
                        <div
                            className={clsx(
                                'symphonyTab',
                                isActive ? 'symphonyTabActive' : 'symphonyTabInactive',
                                tab.id
                            )}
                            style={styx(
                                sty.symphonyTab,
                                isActive && type === 'tabs' ? sty.symphonyTabActive : sty.symphonyTabInactive
                            )}
                            onClick={() => handleOnChange(tab)}
                        >
                            {type === 'numbers' && (
                                tab.renderNumber ?
                                    tab.renderNumber(index)
                                    :
                                    renderTabNumber(index, isActive)
                            )}
                            {type === 'dots' &&
                                <div
                                    className="symphonyTabDot"
                                    style={styx(
                                        sty.symphonyTabDot,
                                        isActive ? { borderColor: Colors.purple } : { borderColor: Colors.grey })
                                    }
                                >
                                    <div
                                        className="symphonyTabDotInner"
                                        style={styx(
                                            sty.symphonyTabDotInner,
                                            isActive ? { backgroundColor: Colors.purple } : { backgroundColor: 'white' }
                                        )}
                                    />
                                </div>
                            }
                            <div
                                className="symphonyTabTitle"
                                style={styx(
                                    sty.symphonyTabTitle,
                                    type === 'tabs' && Sty.text16bold,
                                    isActive ? { color: 'black' } : { color: Colors.greyDark },
                                )}
                            >
                                {tab.title} {tab.isProOnly && <Icon className="tabIsProOnly" icon="gem" color="purple" size={20} style={{ transform: 'translateY(-2px)' }} />}
                            </div>
                        </div>
                        {type === 'numbers' && index < tabs.length - 1 &&
                            <div className="lineSeparator" style={sty.lineSeparator} />
                        }
                    </Fragment>
                );
            })}

            <div
                className="tabsSymphonyRightSideContainer"
                style={sty.tabsSymphonyRightSideContainer}
            >
                {renderLoader()}
                {renderRightSideContent()}
            </div>
        </div>
    )

    if (isHeader) {
        return (
            <div
                className="tabsSymphonyHeader"
                style={styx(
                    sty.tabsSymphonyHeader,
                    styleHeader
                )}
            >
                {tabsComponent}
            </div>
        );
    }

    return tabsComponent
};


const sty: Record<string, CSSProperties> = {
    tabsSymphonyHeader: {
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'row',
        backgroundColor: 'white',
        width: '100%',
        height: 72,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        overflowX: 'scroll',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    tabsSymphonyElement: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        overflowX: 'scroll',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    symphonyTab: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        padding: 12,
        gap: 8,
        position: 'relative',
    },
    symphonyTabActive: {
        paddingLeft: 12,
        paddingRight: 12,
        borderBottom: `2px solid ${Colors.purple}`,
        transition: 'border-color 0.3s ease',
    },
    symphonyTabInactive: {
        paddingLeft: 12,
        paddingRight: 12,
        borderBottom: '2px solid transparent',
        transition: 'border-color 0.3s ease',
    },
    symphonyTabNumber: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        borderRadius: 100,
        backgroundColor: Colors.purple,
        color: 'white',
        fontSize: 12,
        flexShrink: 0,
    },
    symphonyTabTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 14,
        fontWeight: 500,
        transform: 'translateY(2px)',
        whiteSpace: 'nowrap',
    },
    lineSeparator: {
        width: '100%',
        height: 1,
        backgroundColor: Colors.grey,
        marginTop: 'auto',
        marginBottom: 'auto',
    },
    symphonyTabDot: {
        borderRadius: 100,
        borderWidth: 1,
        borderStyle: 'solid',
        padding: 2,
    },
    symphonyTabDotInner: {
        width: 6,
        height: 6,
        borderRadius: 100,
    },
    campaignNumber: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        borderRadius: 100,
        backgroundColor: Colors.purple,
        color: 'white',
        fontSize: 12,
        flexShrink: 0,
    },
    tabsSymphonyRightSideContainer: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        gap: 16
    },
};

export default TabsSymphony;